/**
 * What all menus need in common.
 *
 * Both used to stand several times in the code: the positioning under the
 * button five times, the click beside it six times — each time the same four
 * lines, and each time an opportunity to forget one of them. Here it stands
 * once.
 */

import { useEffect, useLayoutEffect, useRef } from 'preact/hooks'
import type { RefObject } from 'preact'

/**
 * Hangs a menu under its button and keeps it on screen.
 *
 * Measure, then set — that cannot be done declaratively, because the width of
 * the menu is only fixed once it stands there. `useLayoutEffect` instead of
 * `useEffect`, so that the menu is not visible in the wrong place for the
 * length of a frame.
 *
 * @param open    Is the menu open? With `false` nothing happens.
 * @param anchor  The button it shall hang under.
 * @param panel   The menu itself.
 */
export function useAnchored(
  open: boolean,
  anchor: RefObject<HTMLElement>,
  panel: RefObject<HTMLElement>
): void {
  useLayoutEffect(() => {
    if (!open) return
    const a = anchor.current
    const p = panel.current
    if (!a || !p) return

    const place = (): void => {
      const r = a.getBoundingClientRect()
      p.style.top = `${Math.round(r.bottom + 8)}px`
      // Read the width only once the menu is on screen: before that it is 0.
      const w = p.offsetWidth
      p.style.left = `${Math.round(Math.max(10, Math.min(r.left, innerWidth - w - 10)))}px`
    }

    place()
    // When the phone is rotated, the button moves out from under the menu.
    addEventListener('resize', place)
    return () => removeEventListener('resize', place)
  }, [open, anchor, panel])
}

/**
 * Closes on a click beside it and on Escape.
 *
 * `mousedown` and not `click`: whoever clicks beside a menu usually wants to
 * touch something else, and with `click` the menu would still be in the way
 * when the pointer sets down.
 *
 * @param open    Only then is there any listening.
 * @param close   What shall happen on closing.
 * @param inside  Areas that do NOT count as "beside" — the menu itself and the
 *                button that opened it. Without the button, a click on it
 *                would close and immediately open again.
 */
export function useDismiss(
  open: boolean,
  close: () => void,
  ...inside: RefObject<HTMLElement>[]
): void {
  // Via a ref, so that a new `close` on every render does not renew the
  // registration — otherwise the listener loses events in between.
  const fn = useRef(close)
  fn.current = close

  useEffect(() => {
    if (!open) return

    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node | null
      if (!t) return
      for (const r of inside) {
        if (r.current?.contains(t)) return
      }
      fn.current()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') fn.current()
    }

    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
    // `inside` is a new array on every render, but the refs in it stay the
    // same — therefore bound only to `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
}

/**
 * "vor 5 Minuten", "gestern" — easier to grasp than a timestamp.
 */
export function relativeTime(ms: number): string {
  const s = Math.round((Date.now() - ms) / 1000)
  if (s < 60) return 'gerade eben'
  const min = Math.round(s / 60)
  if (min < 60) return `vor ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `vor ${h} Std.`
  const t = Math.round(h / 24)
  if (t === 1) return 'gestern'
  if (t < 7) return `vor ${t} Tagen`
  return new Date(ms).toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })
}
